import type { VarlessCopyId } from '@npc/copy'

import type { DevelopmentTask } from '../../../app/development-tasks'

export const TASK_USER_STATES = [
  'claimable',
  'in_progress',
  'live',
  'needs_action',
  'cancelled',
] as const

export type TaskUserState = (typeof TASK_USER_STATES)[number]

export const INTERNAL_NEXT_ACTIONS = [
  'pending_approval',
  'reviewing',
  'ci_running',
  'v2_handoff',
  'gating',
] as const

export type DevelopmentTaskListQuery = {
  assignee?: string
  user_state?: TaskUserState
  updated_after?: string
}

const USER_STATE_SET = new Set<string>(TASK_USER_STATES)

export function taskUserStateOf(
  task: Pick<DevelopmentTask, 'state'> & {
    user_state?: string | null
    production_verified_at?: string | null
  },
): TaskUserState {
  if (task.user_state && USER_STATE_SET.has(task.user_state)) {
    return task.user_state as TaskUserState
  }
  if (task.state === 'cancelled') return 'cancelled'
  if (task.production_verified_at) return 'live'
  if (task.state === 'failed' || task.state === 'fix_requested') return 'needs_action'
  if (task.state === 'ready_to_claim' || task.state === 'draft') return 'claimable'
  return 'in_progress'
}

export function taskNextActionOf(
  task: Pick<DevelopmentTask, 'state'> & {
    user_state?: string | null
    next_action?: string | null
    production_verified_at?: string | null
  },
): TaskUserState {
  if (task.next_action && USER_STATE_SET.has(task.next_action)) {
    return task.next_action as TaskUserState
  }
  return taskUserStateOf(task)
}

export function taskNeedsRebase(task: {
  needs_rebase?: boolean
  production_verified_at?: string | null
  lineage_relation?: string | null
}): boolean {
  if (task.needs_rebase === true) return true
  if (task.production_verified_at) return false
  return task.lineage_relation === 'missing'
}

export function userStateCopy(state: TaskUserState): VarlessCopyId {
  return `development_task.user_state.${state}`
}

export function isInternalNextAction(value: string | null | undefined): boolean {
  return INTERNAL_NEXT_ACTIONS.some((item) => item === value)
}

export type InboxUpdatedPreset = 'any' | 'hour' | 'today' | 'week'

export type InboxUserFilter = 'all' | TaskUserState

export function inboxUpdatedAfterISO(preset: InboxUpdatedPreset, now = new Date()): string | undefined {
  if (preset === 'any') return undefined
  if (preset === 'hour') return new Date(now.getTime() - 60 * 60 * 1000).toISOString()
  if (preset === 'today') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    return start.toISOString()
  }
  if (preset === 'week') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
  return undefined
}

export function inboxListQuery(input: {
  userState: InboxUserFilter
  assignee: string
  updatedPreset: InboxUpdatedPreset
  now?: Date
}): DevelopmentTaskListQuery {
  const query: DevelopmentTaskListQuery = {}
  if (input.userState !== 'all') query.user_state = input.userState
  const who = input.assignee.trim()
  if (who) query.assignee = who
  const after = inboxUpdatedAfterISO(input.updatedPreset, input.now ?? new Date())
  if (after) query.updated_after = after
  return query
}
